import type { ReportSpecificFilter, ReportTableColumn } from '../report-runtime';

export interface ServiceUsersFilters {
  passengerIds: string[];
  passengerStatuses: string[];
  passengerGroupIds: string[];
  welfareServiceGroupIds: string[];
  hasCompletedService?: boolean;
}
export interface ServiceUsersRow {
  providerId: string;
  providerCode: string;
  providerName: string;
  passengerId: string;
  passengerCode: string;
  passengerName: string;
  passengerPhone: string;
  passengerPictureUrl: string | null;
  passengerStatus: string;
  registeredAt: string;
  providerRelatedAt: string;
  firstBookingAt: string | null;
  passengerGroupIds: string[];
  passengerGroupNames: string[];
  welfareServiceGroupIds: string[];
  welfareServiceGroupNames: string[];
  firstServiceDate: string | null;
  lastServiceDate: string | null;
  bookingCount: number;
  tripCount: number;
  completedTripCount: number;
}
export const serviceUsersDateBasis = 'PASSENGER_CREATED_AT';
export const serviceUsersSorts = ['registeredAt', 'passengerName', 'passengerCode'] as const;
export const serviceUsersSpecificFilters = [
  { id: 'passengerIds', type: 'uuidMulti' },
  { id: 'passengerStatuses', type: 'multi', values: ['ACTIVE', 'INACTIVE', 'SUSPENDED'] },
  { id: 'passengerGroupIds', type: 'uuidMulti' },
  { id: 'welfareServiceGroupIds', type: 'uuidMulti' },
  { id: 'hasCompletedService', type: 'boolean' },
] as const satisfies readonly ReportSpecificFilter[];
const meanings: Record<string, string> = {
  registered_at: 'suMeaningRegistration',
  date_basis: 'suMeaningRegistration',
  provider_related_at: 'suMeaningRelation',
  first_booking_at: 'suMeaningBooking',
  passenger_group_ids: 'suMeaningGroups',
  passenger_group_names: 'suMeaningGroups',
  welfare_service_group_ids: 'suMeaningWelfare',
  welfare_service_group_names: 'suMeaningWelfare',
  group_evidence_basis: 'suMeaningGroups',
  group_visibility: 'suMeaningWelfare',
  area_evidence_basis: 'suMeaningArea',
  first_service_date: 'suMeaningCompletion',
  last_service_date: 'suMeaningCompletion',
  service_history_basis: 'suMeaningCompletion',
  booking_count: 'suMeaningBookingCount',
  trip_count: 'suMeaningTripCount',
  completed_trip_count: 'suMeaningCompletedCount',
};
const sortKeys: Record<string, string> = {
  registered_at: 'registeredAt',
  passenger_code: 'passengerCode',
  passenger_name: 'passengerName',
};
const column = (
  id: string,
  type: ReportTableColumn['type'],
  source: string,
  empty = 'suEmptyNever',
  pii = false,
  sortable = false,
): ReportTableColumn => ({
  id,
  type,
  labelKey: `suColumn_${id}`,
  source,
  emptyMeaning: empty,
  meaningKey: meanings[id],
  sortKey: sortKeys[id],
  pii,
  sortable,
});
export const serviceUsersColumns = [
  column('provider_id', 'text', 'suSourceProvider'),
  column('provider_code', 'text', 'suSourceProvider'),
  column('provider_name', 'text', 'suSourceProvider'),
  column('passenger_id', 'text', 'suSourcePassenger', 'suEmptyNever', true),
  column('passenger_code', 'text', 'suSourcePassenger', 'suEmptyNever', true, true),
  column('passenger_name', 'text', 'suSourcePassenger', 'suEmptyNever', true, true),
  column('passenger_phone', 'text', 'suSourcePassenger', 'suEmptyNever', true),
  column('passenger_status', 'text', 'suSourcePassenger'),
  column('registered_at', 'timestamp', 'suSourceRegistration', 'suEmptyNever', false, true),
  column('date_basis', 'text', 'suSourceRegistration'),
  column('provider_related_at', 'timestamp', 'suSourceRelation'),
  column('first_booking_at', 'timestamp', 'suSourceBooking', 'suEmptyBooking'),
  column('passenger_group_ids', 'list', 'suSourceMembership', 'suEmptyGroups', true),
  column('passenger_group_names', 'list', 'suSourceMembership', 'suEmptyGroups', true),
  column('welfare_service_group_ids', 'list', 'suSourceEligibility', 'suEmptyGroups', true),
  column('welfare_service_group_names', 'list', 'suSourceEligibility', 'suEmptyGroups', true),
  column('group_evidence_basis', 'text', 'suSourceMembership'),
  column('group_visibility', 'text', 'suSourceMembership'),
  column('area_evidence_basis', 'text', 'suSourceHistory'),
  column('first_service_date', 'timestamp', 'suSourceCompletion', 'suEmptyCompleted'),
  column('last_service_date', 'timestamp', 'suSourceCompletion', 'suEmptyCompleted'),
  column('service_history_basis', 'text', 'suSourceCompletion'),
  { ...column('booking_count', 'number', 'suSourceBooking'), unitKey: 'unit_count' },
  { ...column('trip_count', 'number', 'suSourceHistory'), unitKey: 'unit_count' },
  { ...column('completed_trip_count', 'number', 'suSourceCompletion'), unitKey: 'unit_count' },
] as const satisfies readonly ReportTableColumn[];
export const serviceUsersDefaultColumns = [
  'provider_name',
  'passenger_code',
  'passenger_name',
  'passenger_phone',
  'passenger_status',
  'registered_at',
  'passenger_group_names',
  'welfare_service_group_names',
  'first_service_date',
  'last_service_date',
  'booking_count',
  'trip_count',
  'completed_trip_count',
] as const;
